import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { useLanguage } from "@/context/LanguageContext";
import { READING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Emails a copy of the finished reading; the address is sent once and never stored.
export const EmailMemoForm = ({ payload, onClose }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value) || value.length > 254) {
      setError(t.reading.emailMemoInvalid);
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API}/send_reading`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, email: value }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(t.reading.emailMemoSent);
      setEmail("");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t.reading.emailMemoFailed);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={handleSubmit}
      data-testid={READING.emailMemoForm}
      className="mt-4 rounded-md border border-gold/40 bg-gold/5 p-5"
    >
      <div className="text-[0.7rem] tracking-[0.35em] uppercase text-gold font-bold mb-3">
        ✉ {t.reading.emailMemoTitle}
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          placeholder={t.contact.emailPlaceholder}
          maxLength={254}
          autoFocus
          data-testid={READING.emailMemoInput}
          className="flex-1 bg-oxblood-surface border border-gold/25 rounded-md px-4 py-2.5 font-serif text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold/70 transition-colors duration-300"
        />
        <button
          type="submit"
          disabled={sending}
          data-testid={READING.emailMemoSend}
          className="px-5 py-2.5 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase font-semibold disabled:opacity-40"
        >
          {sending ? t.reading.emailMemoSending : t.reading.emailMemoSend}
        </button>
      </div>
      {error && (
        <p data-testid="reading-email-memo-error" className="mt-2 text-sm text-red-300 font-serif italic">
          {error}
        </p>
      )}
      <p className="mt-3 text-xs text-ivory/50 font-serif italic">{t.reading.emailMemoHint}</p>
    </motion.form>
  );
};

export default EmailMemoForm;
